// src/utils/countryFlag.js
//
// Resuelve un valor de "country" arbitrario (lo que viene en profile.country)
// al código ISO-3166-1 alpha-2 en minúsculas, que es lo que necesita flagcdn.com
// para servir la bandera.
//
// El campo profile.country del proyecto a veces guarda el código ("AR"),
// a veces el nombre en español ("Argentina", "México") y a veces strings sueltos
// (por la base histórica). Este helper normaliza todo y devuelve `null` si no
// reconoce el país (así el componente FlagBadge puede ocultarse sin romper).

const NAME_TO_CODE = {
  // Norteamérica
  'estados unidos': 'us',
  'estados unidos (alaska)': 'us',
  'eeuu': 'us',
  'ee.uu.': 'us',
  'usa': 'us',
  'united states': 'us',
  'canada': 'ca',
  'canadá': 'ca',
  'mexico': 'mx',
  'méxico': 'mx',

  // Centroamérica
  'guatemala': 'gt',
  'belice': 'bz',
  'belize': 'bz',
  'el salvador': 'sv',
  'honduras': 'hn',
  'nicaragua': 'ni',
  'costa rica': 'cr',
  'panama': 'pa',
  'panamá': 'pa',

  // Sudamérica
  'colombia': 'co',
  'venezuela': 've',
  'guyana': 'gy',
  'surinam': 'sr',
  'suriname': 'sr',
  'guyana francesa': 'gf',
  'ecuador': 'ec',
  'peru': 'pe',
  'perú': 'pe',
  'brasil': 'br',
  'brazil': 'br',
  'bolivia': 'bo',
  'paraguay': 'py',
  'chile': 'cl',
  'argentina': 'ar',
  'uruguay': 'uy',

  // Caribe
  'cuba': 'cu',
  'republica dominicana': 'do',
  'república dominicana': 'do',
  'puerto rico': 'pr',
  'haiti': 'ht',
  'haití': 'ht',
  'jamaica': 'jm',
  'bahamas': 'bs',
  'trinidad y tobago': 'tt',
  'barbados': 'bb',
  'santa lucia': 'lc',
  'santa lucía': 'lc',
  'san vicente y las granadinas': 'vc',
  'granada': 'gd',
  'san cristobal y nieves': 'kn',
  'san cristóbal y nieves': 'kn',
  'antigua y barbuda': 'ag'
};

const VALID_CODES = new Set(Object.values(NAME_TO_CODE));

/**
 * Devuelve el código ISO alpha-2 en minúsculas para un valor de "country".
 * Acepta: códigos ISO ("AR", "us", "US-AK"), nombres en español o inglés
 * con o sin acentos. Devuelve null si no se reconoce.
 */
export function resolveCountryCode(input) {
  if (!input) return null;
  const raw = String(input).trim();
  if (!raw) return null;

  // Caso 1: ya es código ISO alpha-2 (con guión opcional, ej "US-AK")
  const isoMatch = raw.match(/^([A-Za-z]{2})(?:-[A-Za-z0-9]+)?$/);
  if (isoMatch) {
    const code = isoMatch[1].toLowerCase();
    if (VALID_CODES.has(code)) return code;
  }

  // Caso 2: nombre en español/inglés — normalizar (lowercase + sin acentos)
  const stripDiacritics = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
  const normalized = stripDiacritics(raw.toLowerCase());

  // Búsqueda directa
  if (NAME_TO_CODE[raw.toLowerCase()]) return NAME_TO_CODE[raw.toLowerCase()];

  // Búsqueda por nombre normalizado vs cada key normalizada
  for (const [name, code] of Object.entries(NAME_TO_CODE)) {
    if (stripDiacritics(name) === normalized) return code;
  }

  return null;
}

/**
 * URL de la bandera en flagcdn.com (SVG). Tamaño exacto se controla via CSS.
 */
export function getFlagSvgUrl(code) {
  if (!code) return null;
  return `https://flagcdn.com/${code}.svg`;
}
